import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactPaginate from "react-paginate";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import { Puff } from "react-loader-spinner";
import Breadcrumb from "components/Common/Breadcrumb";
import {
  ConnectedStore,
  ConnectedStoreAddress,
  UpdateConnectedStoreRequest,
} from "models/ConnectedStore";
import { AdminService } from "services/AdminService";
import { toast } from "react-toastify";
import CustomGooglePlacesAutocomplete from "components/Common/auto-complete";
import { geocodeByPlaceId } from "react-google-places-autocomplete";
import { environment } from "environment/environment";
import { useNavigate } from "react-router-dom";
import PlatformBadge from "./PlatformBadge";
import "./StoresAddModal.css";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;
const CUSTOMER_LIVE_SEARCH_MS = 280;

type LinkCustomerPreview = {
  customerId: string;
  name: string;
  email: string;
  phoneNumber: string;
  businessName: string | null;
  address: ConnectedStoreAddress | null;
  suggestedStoreName?: string;
};

const DELIVERY_METHODS = [
  { value: "drop_at_door", label: "Drop at Door" },
  { value: "drop_at_mailbox", label: "Drop at Mailbox" },
  { value: "drop_at_front_desk", label: "Drop at Front Desk" },
  { value: "return", label: "Return if Delivery Fails" },
  { value: "reattempt", label: "Reattempt if Delivery Fails" },
  { value: "call_me_to_confirm", label: "Call to Confirm" },
];

const emptyAddress: ConnectedStoreAddress = {
  street: "",
  unit: "",
  city: "",
  postalCode: "",
};

const addressSearchOptions = {
  componentRestrictions: { country: "ca" },
  locationRestriction: {
    north: 56.85,
    south: 41.6,
    east: -74.0,
    west: -95.15,
  },
};

const formatAddress = (address?: ConnectedStoreAddress | null): string => {
  if (!address) return "Not configured";
  return [
    address.street,
    address.unit ? `Unit ${address.unit}` : "",
    address.city,
    address.postalCode,
  ]
    .filter(Boolean)
    .join(", ");
};

const Stores: React.FC = () => {
  const navigate = useNavigate();
  const [stores, setStores] = useState<ConnectedStore[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [editingStore, setEditingStore] = useState<ConnectedStore | null>(null);
  const [form, setForm] = useState<UpdateConnectedStoreRequest>({
    storeName: "",
    defaultPickupAddress: emptyAddress,
    defaultDeliveryMethod: "drop_at_door",
    weightChargesEnabled: false,
    freeWeightUpToLb: 0,
    additionalPricePerLb: 0,
    taxEnabled: true,
  });
  const [saving, setSaving] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [addStoreMode, setAddStoreMode] = useState<"invite" | "existing">(
    "invite"
  );
  const [inviteTtlHours, setInviteTtlHours] = useState(72);
  const [inviteCreating, setInviteCreating] = useState(false);
  const [createdInviteUrl, setCreatedInviteUrl] = useState("");
  const [createdExpiresAt, setCreatedExpiresAt] = useState("");
  const [copied, setCopied] = useState(false);
  const [linkCustomerId, setLinkCustomerId] = useState("");
  const [linkStoreName, setLinkStoreName] = useState("");
  const [linkPickupAddress, setLinkPickupAddress] =
    useState<ConnectedStoreAddress>(emptyAddress);
  const [linkLookingUp, setLinkLookingUp] = useState(false);
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const [linkCustomerPreview, setLinkCustomerPreview] =
    useState<LinkCustomerPreview | null>(null);
  const [linkSearchResults, setLinkSearchResults] = useState<
    LinkCustomerPreview[]
  >([]);
  const [linkSearchOpen, setLinkSearchOpen] = useState(false);
  const [linkSearchEmpty, setLinkSearchEmpty] = useState(false);
  const linkSearchSeq = useRef(0);

  const applyLinkCustomer = useCallback((preview: LinkCustomerPreview) => {
    setLinkCustomerId(preview.customerId);
    setLinkCustomerPreview(preview);
    setLinkStoreName(
      preview.suggestedStoreName ||
        preview.businessName ||
        preview.name ||
        preview.customerId
    );
    if (preview.address?.street && preview.address?.city) {
      setLinkPickupAddress({
        ...emptyAddress,
        ...preview.address,
      });
    } else {
      setLinkPickupAddress(emptyAddress);
    }
    setLinkSearchResults([]);
    setLinkSearchOpen(false);
    setLinkSearchEmpty(false);
  }, []);

  const resetInviteModal = () => {
    setAddStoreMode("invite");
    setInviteTtlHours(72);
    setInviteCreating(false);
    setCreatedInviteUrl("");
    setCreatedExpiresAt("");
    setCopied(false);
    setLinkCustomerId("");
    setLinkStoreName("");
    setLinkPickupAddress(emptyAddress);
    setLinkLookingUp(false);
    setLinkSubmitting(false);
    setLinkCustomerPreview(null);
    setLinkSearchResults([]);
    setLinkSearchOpen(false);
    setLinkSearchEmpty(false);
    linkSearchSeq.current += 1;
  };

  const openInviteModal = () => {
    resetInviteModal();
    setInviteOpen(true);
  };

  const closeInviteModal = (force = false) => {
    if (!force && (inviteCreating || linkSubmitting)) {
      return;
    }
    setInviteOpen(false);
    resetInviteModal();
  };

  const createInviteLink = async () => {
    setInviteCreating(true);
    setCopied(false);
    try {
      const response = await AdminService.createCustomStoreInvite({
        expiresInHours: inviteTtlHours,
      });
      if (!response.success || !response.data?.inviteUrl) {
        throw new Error(
          (response as { error?: string }).error ||
            response.message ||
            "Unable to create invite link."
        );
      }
      setCreatedInviteUrl(response.data.inviteUrl);
      setCreatedExpiresAt(String(response.data.expiresAt || ""));
      toast.success("Invite link created.");
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error ||
          error?.message ||
          "Unable to create invite link."
      );
    } finally {
      setInviteCreating(false);
    }
  };

  const copyInviteLink = async () => {
    if (!createdInviteUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(createdInviteUrl);
      setCopied(true);
      toast.success("Link copied.");
    } catch {
      toast.error("Could not copy. Select the link and copy it manually.");
    }
  };

  const mapSearchHit = (hit: {
    customer: {
      customerId: string;
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber: string;
      address: ConnectedStoreAddress | null;
    };
    businessName: string | null;
    suggestedStoreName: string;
  }): LinkCustomerPreview => ({
    customerId: hit.customer.customerId,
    name: `${hit.customer.firstName || ""} ${hit.customer.lastName || ""}`.trim(),
    email: hit.customer.email || "",
    phoneNumber: hit.customer.phoneNumber || "",
    businessName: hit.businessName || null,
    address: hit.customer.address || null,
    suggestedStoreName: hit.suggestedStoreName,
  });

  const lookupExistingCustomer = async () => {
    const customerId = linkCustomerId.trim();
    if (customerId.length < 2) {
      toast.error("Enter a Zulu customer ID to search.");
      return;
    }
    if (
      linkCustomerPreview &&
      linkCustomerPreview.customerId.toLowerCase() === customerId.toLowerCase()
    ) {
      return;
    }
    const exact = linkSearchResults.find(
      item => item.customerId.toLowerCase() === customerId.toLowerCase()
    );
    if (exact) {
      applyLinkCustomer(exact);
      return;
    }
    setLinkLookingUp(true);
    try {
      const response =
        await AdminService.lookupCustomStoreCustomerByZuluId(customerId);
      if (!response.success || !response.data?.customer) {
        throw new Error(
          (response as { error?: string }).error ||
            response.message ||
            "Customer not found."
        );
      }
      applyLinkCustomer(mapSearchHit(response.data));
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error ||
          error?.message ||
          "Unable to look up that Zulu customer."
      );
    } finally {
      setLinkLookingUp(false);
    }
  };

  useEffect(() => {
    if (!inviteOpen || addStoreMode !== "existing") {
      return;
    }
    const query = linkCustomerId.trim();
    if (
      linkCustomerPreview &&
      linkCustomerPreview.customerId.toLowerCase() === query.toLowerCase()
    ) {
      setLinkSearchResults([]);
      setLinkSearchOpen(false);
      setLinkSearchEmpty(false);
      return;
    }
    if (query.length < 2) {
      setLinkSearchResults([]);
      setLinkSearchOpen(false);
      setLinkSearchEmpty(false);
      setLinkLookingUp(false);
      return;
    }

    const seq = ++linkSearchSeq.current;
    const timer = window.setTimeout(async () => {
      setLinkLookingUp(true);
      try {
        const response = await AdminService.searchCustomStoreCustomers(query);
        if (seq !== linkSearchSeq.current) {
          return;
        }
        if (!response.success) {
          setLinkSearchResults([]);
          setLinkSearchOpen(true);
          setLinkSearchEmpty(true);
          return;
        }
        const mapped = (response.data?.results || []).map(mapSearchHit);
        setLinkSearchResults(mapped);
        setLinkSearchOpen(true);
        setLinkSearchEmpty(mapped.length === 0);
      } catch (_error) {
        if (seq !== linkSearchSeq.current) {
          return;
        }
        setLinkSearchResults([]);
        setLinkSearchOpen(true);
        setLinkSearchEmpty(true);
      } finally {
        if (seq === linkSearchSeq.current) {
          setLinkLookingUp(false);
        }
      }
    }, CUSTOMER_LIVE_SEARCH_MS);

    return () => window.clearTimeout(timer);
  }, [inviteOpen, addStoreMode, linkCustomerId, linkCustomerPreview]);

  const selectLinkPickupAddress = async (_label: string, placeId: string) => {
    if (!placeId) return;
    try {
      const [place] = await geocodeByPlaceId(placeId);
      const component = (type: string) =>
        place.address_components.find(item => item.types.includes(type))
          ?.long_name || "";
      const street = [component("street_number"), component("route")]
        .filter(Boolean)
        .join(" ");
      const city =
        component("locality") ||
        component("postal_town") ||
        component("administrative_area_level_2");
      const postalCode = component("postal_code").toUpperCase();
      setLinkPickupAddress(current => ({
        ...current,
        street: street || current.street,
        city: city || current.city,
        postalCode: postalCode || current.postalCode,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      }));
    } catch (_error) {
      toast.error("Could not read that pickup address. Please try again.");
    }
  };

  const linkExistingCustomer = async () => {
    if (!linkCustomerPreview) {
      toast.error("Look up a Zulu customer first.");
      return;
    }
    if (linkStoreName.trim().length < 2) {
      toast.error("Store name must contain at least 2 characters.");
      return;
    }
    if (
      !linkPickupAddress.street?.trim() ||
      !linkPickupAddress.city?.trim() ||
      !Number.isFinite(Number(linkPickupAddress.lat)) ||
      !Number.isFinite(Number(linkPickupAddress.lng))
    ) {
      toast.error("Search and select a default pickup location.");
      return;
    }

    setLinkSubmitting(true);
    try {
      const response = await AdminService.linkExistingCustomStoreCustomer({
        customerId: linkCustomerPreview.customerId,
        storeName: linkStoreName.trim(),
        defaultDeliveryMethod: "drop_at_door",
        defaultPickupAddress: linkPickupAddress,
      });
      if (!response.success || !response.data?.storeId) {
        throw new Error(
          (response as { error?: string }).error ||
            response.message ||
            "Unable to link customer."
        );
      }
      toast.success(
        response.data.alreadyLinked
          ? "Customer already linked; store updated."
          : "Existing customer linked as a custom store."
      );
      closeInviteModal(true);
      loadStores();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.error ||
          error?.message ||
          "Unable to link that Zulu customer."
      );
    } finally {
      setLinkSubmitting(false);
    }
  };

  const inviteModalBusy = inviteCreating || linkSubmitting;

  const loadStores = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AdminService.getConnectedShopifyStores(
        PAGE_SIZE,
        page,
        appliedSearch
      );
      if (!response.success) {
        throw new Error(response.message || "Unable to load stores.");
      }
      setStores(response.data?.storeList || []);
      setTotal(response.data?.count || 0);
    } catch (error: any) {
      setStores([]);
      setTotal(0);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to load connected stores."
      );
    } finally {
      setLoading(false);
    }
  }, [page, appliedSearch]);

  useEffect(() => {
    document.title = "Stores | Zulu Courier";
  }, []);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  useEffect(() => {
    const refreshVisibleStores = () => {
      if (document.visibilityState === "visible") {
        loadStores();
      }
    };
    document.addEventListener("visibilitychange", refreshVisibleStores);
    return () =>
      document.removeEventListener("visibilitychange", refreshVisibleStores);
  }, [loadStores]);

  // Live filter: wait briefly after typing stops, then search by shop name.
  useEffect(() => {
    const trimmed = searchTerm.trim();
    const timer = window.setTimeout(() => {
      setAppliedSearch(trimmed);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  const clearSearch = () => {
    setSearchTerm("");
    setAppliedSearch("");
    setPage(1);
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);

  const openDetails = (store: ConnectedStore) => {
    navigate(`/super_admin/stores/${store.id}/view`);
  };

  const openEdit = (store: ConnectedStore) => {
    navigate(`/super_admin/stores/${store.id}/pricing`);
  };

  const updateAddress = (
    field: keyof ConnectedStoreAddress,
    value: string
  ) => {
    setForm(current => ({
      ...current,
      defaultPickupAddress: {
        ...current.defaultPickupAddress,
        [field]: value,
      },
    }));
  };

  const selectPickupAddress = async (_label: string, placeId: string) => {
    if (!placeId) return;
    try {
      const [place] = await geocodeByPlaceId(placeId);
      const component = (type: string) =>
        place.address_components.find(item => item.types.includes(type))
          ?.long_name || "";
      const street = [
        component("street_number"),
        component("route"),
      ]
        .filter(Boolean)
        .join(" ");
      const city =
        component("locality") ||
        component("postal_town") ||
        component("administrative_area_level_2");
      const postalCode = component("postal_code").toUpperCase();
      setForm(current => ({
        ...current,
        defaultPickupAddress: {
          ...current.defaultPickupAddress,
          street: street || current.defaultPickupAddress.street,
          city: city || current.defaultPickupAddress.city,
          postalCode: postalCode || current.defaultPickupAddress.postalCode,
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        },
      }));
    } catch (_error) {
      toast.error("Could not read that pickup address. Please try again.");
    }
  };

  const saveStore = async () => {
    if (!editingStore) return;
    if (form.storeName.trim().length < 2) {
      toast.error("Store name must contain at least 2 characters.");
      return;
    }
    if (
      !form.defaultPickupAddress.street?.trim() ||
      !form.defaultPickupAddress.city?.trim()
    ) {
      toast.error("Pickup street and city are required.");
      return;
    }
    if (
      form.weightChargesEnabled &&
      (!Number.isFinite(form.freeWeightUpToLb) ||
        form.freeWeightUpToLb < 0 ||
        !Number.isFinite(form.additionalPricePerLb) ||
        form.additionalPricePerLb < 0)
    ) {
      toast.error("Weight pricing values must be valid non-negative numbers.");
      return;
    }

    setSaving(true);
    try {
      const response = await AdminService.updateConnectedShopifyStore(
        editingStore.id,
        {
          ...form,
          storeName: form.storeName.trim(),
        }
      );
      if (!response.success) {
        throw new Error(response.message || "Unable to update store.");
      }
      toast.success("Store information updated.");
      setEditingStore(null);
      await loadStores();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to update store."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Admin" breadcrumbItem="Stores" />
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
                  <div>
                    <h4 className="card-title mb-1">Connected Stores</h4>
                    <p className="text-muted mb-0">
                      Stores currently connected to Zulu
                    </p>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Button color="primary" onClick={openInviteModal}>
                      <i className="bx bx-plus me-1" />
                      Add new store
                    </Button>
                    <Badge color="primary" pill className="font-size-12 px-3 py-2">
                      {total} {total === 1 ? "store" : "stores"}
                    </Badge>
                  </div>
                </div>

                <div className="mb-4" style={{ maxWidth: 360 }}>
                  <InputGroup>
                    <InputGroupText>
                      <i className="bx bx-search" />
                    </InputGroupText>
                    <Input
                      type="text"
                      placeholder="Search by shop name"
                      aria-label="Search stores by shop name"
                      value={searchTerm}
                      maxLength={120}
                      onChange={event => setSearchTerm(event.target.value)}
                    />
                    {searchTerm ? (
                      <InputGroupText
                        role="button"
                        aria-label="Clear search"
                        title="Clear search"
                        onClick={clearSearch}
                        style={{ cursor: "pointer" }}
                      >
                        <i className="bx bx-x font-size-18" />
                      </InputGroupText>
                    ) : null}
                  </InputGroup>
                </div>

                {loading ? (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ height: "500px" }}
                  >
                    <Puff color="#FFD0B8" height={50} width={50} />
                  </div>
                ) : stores.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <i className="bx bx-store-alt font-size-24 d-block mb-2" />
                    {appliedSearch ? (
                      <>
                        No stores match &ldquo;{appliedSearch}&rdquo;.{" "}
                        <Button color="link" className="p-0" onClick={clearSearch}>
                          Clear search
                        </Button>
                      </>
                    ) : (
                      "No connected stores found."
                    )}
                  </div>
                ) : (
                    <div className="table-responsive">
                      <Table className="table-nowrap align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Store Name</th>
                            <th>Platform</th>
                            <th>Connection Status</th>
                            <th>Pricing Status</th>
                            <th className="text-end">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stores.map(store => (
                            <tr key={store.id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="avatar-xs me-3">
                                    <span className="avatar-title rounded bg-success bg-soft text-success">
                                      <i className="bx bx-store-alt font-size-18" />
                                    </span>
                                  </div>
                                  <div>
                                    <h5 className="font-size-14 mb-0">
                                      {store.storeName}
                                    </h5>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <PlatformBadge platform={store.platform} />
                              </td>
                              <td>
                                <Badge color="success" pill>
                                  {store.connectionStatus}
                                </Badge>
                              </td>
                              <td>
                                <Badge
                                  color={
                                    store.pricingStatus === "active"
                                      ? "success"
                                      : "warning"
                                  }
                                  pill
                                >
                                  {store.pricingStatus}
                                </Badge>
                              </td>
                              <td className="text-end">
                                <div className="d-inline-flex align-items-center gap-2">
                                  <Button
                                    type="button"
                                    color="light"
                                    size="sm"
                                    aria-label="Store details"
                                    title="Store details"
                                    onClick={() => openDetails(store)}
                                  >
                                    <i className="bx bx-show font-size-16" />
                                  </Button>
                                  <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => openEdit(store)}
                                  >
                                    <i className="bx bx-edit-alt me-1" />
                                    Edit
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                )}

                {!loading && total > 0 && (
                  <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mt-4 pt-3 border-top">
                    <span className="text-muted mb-0">
                      Showing {rangeStart}–{rangeEnd} of {total}
                    </span>
                    {pageCount > 1 && (
                      <ReactPaginate
                        previousLabel="Previous"
                        nextLabel="Next"
                        breakLabel="…"
                        pageCount={pageCount}
                        marginPagesDisplayed={1}
                        pageRangeDisplayed={2}
                        onPageChange={({ selected }) => setPage(selected + 1)}
                        forcePage={Math.min(page, pageCount) - 1}
                        containerClassName="pagination mb-0 flex-nowrap"
                        pageClassName="page-item"
                        pageLinkClassName="page-link"
                        previousClassName="page-item"
                        previousLinkClassName="page-link"
                        nextClassName="page-item"
                        nextLinkClassName="page-link"
                        breakClassName="page-item"
                        breakLinkClassName="page-link"
                        disabledClassName="disabled"
                        activeClassName="active"
                      />
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal
          isOpen={Boolean(editingStore)}
          toggle={() => !saving && setEditingStore(null)}
          centered
          size="lg"
        >
          <ModalHeader toggle={() => !saving && setEditingStore(null)}>
            Edit Store
          </ModalHeader>
          <ModalBody>
            <h5 className="font-size-15 mb-4">Store Information</h5>
            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label for="storeName">Store Name</Label>
                  <Input
                    id="storeName"
                    value={form.storeName}
                    maxLength={120}
                    onChange={event =>
                      setForm(current => ({
                        ...current,
                        storeName: event.target.value,
                      }))
                    }
                  />
                </FormGroup>
              </Col>
            </Row>

            <Label className="mb-2">Default Pickup Address</Label>
            <div className="text-muted font-size-13 mb-2">
              Current: {formatAddress(form.defaultPickupAddress)}
            </div>
            <Row>
              <Col md={9}>
                <FormGroup>
                  <CustomGooglePlacesAutocomplete
                    apiKey={environment.google_maps_key}
                    autocompletionRequest={addressSearchOptions}
                    addressType="pickup"
                    handleAddress={selectPickupAddress}
                  />
                  <small className="text-muted">
                    Search and select a new address to replace the current one.
                  </small>
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Input
                    aria-label="Pickup unit"
                    placeholder="Unit"
                    value={form.defaultPickupAddress.unit || ""}
                    maxLength={40}
                    onChange={event => updateAddress("unit", event.target.value)}
                  />
                </FormGroup>
              </Col>
            </Row>

            <FormGroup className="mb-0">
              <Label for="defaultDeliveryMethod">
                Default Delivery Method
              </Label>
              <Input
                id="defaultDeliveryMethod"
                type="select"
                value={form.defaultDeliveryMethod}
                onChange={event =>
                  setForm(current => ({
                    ...current,
                    defaultDeliveryMethod: event.target.value,
                  }))
                }
              >
                {DELIVERY_METHODS.map(method => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </Input>
            </FormGroup>

            <hr className="my-4" />
            <h5 className="font-size-15 mb-3">Weight Charges</h5>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="weightChargesEnabled">
                    Apply Weight Charges
                  </Label>
                  <Input
                    id="weightChargesEnabled"
                    type="select"
                    value={form.weightChargesEnabled ? "yes" : "no"}
                    onChange={event =>
                      setForm(current => ({
                        ...current,
                        weightChargesEnabled: event.target.value === "yes",
                      }))
                    }
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="taxEnabled">Apply Tax</Label>
                  <Input
                    id="taxEnabled"
                    type="select"
                    value={form.taxEnabled ? "yes" : "no"}
                    onChange={event =>
                      setForm(current => ({
                        ...current,
                        taxEnabled: event.target.value === "yes",
                      }))
                    }
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            {form.weightChargesEnabled && (
              <Row>
                <Col md={6}>
                  <FormGroup className="mb-0">
                    <Label for="freeWeightUpToLb">
                      Free Weight Up To (lb)
                    </Label>
                    <Input
                      id="freeWeightUpToLb"
                      type="number"
                      min={0}
                      step={0.01}
                      value={form.freeWeightUpToLb}
                      onChange={event =>
                        setForm(current => ({
                          ...current,
                          freeWeightUpToLb: Number(event.target.value),
                        }))
                      }
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup className="mb-0">
                    <Label for="additionalPricePerLb">
                      Additional Price Per lb
                    </Label>
                    <Input
                      id="additionalPricePerLb"
                      type="number"
                      min={0}
                      step={0.01}
                      value={form.additionalPricePerLb}
                      onChange={event =>
                        setForm(current => ({
                          ...current,
                          additionalPricePerLb: Number(event.target.value),
                        }))
                      }
                    />
                  </FormGroup>
                </Col>
              </Row>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              outline
              disabled={saving}
              onClick={() => setEditingStore(null)}
            >
              Cancel
            </Button>
            <Button color="primary" disabled={saving} onClick={saveStore}>
              {saving && (
                <Puff color="#FFD0B8" height={18} width={18} wrapperClass="me-2" />
              )}
              Save Changes
            </Button>
          </ModalFooter>
        </Modal>

        <Modal
          isOpen={inviteOpen}
          toggle={() => !inviteModalBusy && closeInviteModal()}
          centered
          size="lg"
          className="add-store-modal"
        >
          <ModalHeader toggle={() => !inviteModalBusy && closeInviteModal()}>
            Add new custom store
          </ModalHeader>
          <ModalBody>
            {!createdInviteUrl ? (
              <>
                <div
                  className="add-store-options"
                  role="tablist"
                  aria-label="How to add the store"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={addStoreMode === "invite"}
                    className={`add-store-option${
                      addStoreMode === "invite" ? " is-active" : ""
                    }`}
                    disabled={inviteModalBusy}
                    onClick={() => setAddStoreMode("invite")}
                  >
                    <span className="add-store-option__icon" aria-hidden>
                      <i className="bx bx-link-alt" />
                    </span>
                    <span>
                      <span className="add-store-option__title">
                        New invite link
                      </span>
                      <span className="add-store-option__desc">
                        Send a one-time link so a new merchant can onboard
                        themselves.
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={addStoreMode === "existing"}
                    className={`add-store-option${
                      addStoreMode === "existing" ? " is-active" : ""
                    }`}
                    disabled={inviteModalBusy}
                    onClick={() => setAddStoreMode("existing")}
                  >
                    <span className="add-store-option__icon" aria-hidden>
                      <i className="bx bx-user-check" />
                    </span>
                    <span>
                      <span className="add-store-option__title">
                        Link existing customer
                      </span>
                      <span className="add-store-option__desc">
                        Connect a verified Zulu business customer by their
                        customer ID.
                      </span>
                    </span>
                  </button>
                </div>

                {addStoreMode === "invite" ? (
                  <div className="add-store-panel">
                    <Label
                      for="inviteTtlHours"
                      className="add-store-panel__label"
                    >
                      Link expires in
                    </Label>
                    <Input
                      id="inviteTtlHours"
                      type="select"
                      value={inviteTtlHours}
                      onChange={event =>
                        setInviteTtlHours(Number(event.target.value) || 72)
                      }
                      disabled={inviteCreating}
                    >
                      <option value={24}>24 hours</option>
                      <option value={48}>48 hours</option>
                      <option value={72}>72 hours</option>
                      <option value={168}>7 days</option>
                    </Input>
                    <span className="add-store-panel__hint">
                      Creates a single-use onboarding link. Share it with the
                      merchant — it can only be used once.
                    </span>
                  </div>
                ) : (
                  <div className="add-store-panel">
                    <Label
                      for="linkCustomerId"
                      className="add-store-panel__label"
                    >
                      Search by Zulu customer ID
                    </Label>
                    <div className="add-store-lookup-wrap">
                      <div className="add-store-lookup">
                        <div className="add-store-lookup__field">
                          <i
                            className="bx bx-search-alt add-store-lookup__icon"
                            aria-hidden
                          />
                          <Input
                            id="linkCustomerId"
                            type="text"
                            placeholder="Start typing a customer ID…"
                            value={linkCustomerId}
                            maxLength={40}
                            autoComplete="off"
                            disabled={linkSubmitting}
                            onChange={event => {
                              setLinkCustomerId(event.target.value);
                              setLinkCustomerPreview(null);
                              setLinkSearchOpen(true);
                            }}
                            onFocus={() => {
                              if (
                                linkSearchResults.length > 0 ||
                                linkSearchEmpty
                              ) {
                                setLinkSearchOpen(true);
                              }
                            }}
                            onBlur={() => {
                              window.setTimeout(
                                () => setLinkSearchOpen(false),
                                150
                              );
                            }}
                            onKeyDown={event => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                if (linkSearchResults.length === 1) {
                                  applyLinkCustomer(linkSearchResults[0]);
                                } else {
                                  lookupExistingCustomer();
                                }
                              }
                              if (event.key === "Escape") {
                                setLinkSearchOpen(false);
                              }
                            }}
                          />
                          {linkLookingUp ? (
                            <span className="add-store-lookup__spinner">
                              <Puff color="#f1734f" height={18} width={18} />
                            </span>
                          ) : null}
                        </div>
                      </div>
                      {linkSearchOpen &&
                      !linkCustomerPreview &&
                      linkCustomerId.trim().length >= 2 ? (
                        <div
                          className="add-store-live-results"
                          role="listbox"
                          aria-label="Matching customers"
                        >
                          {linkSearchResults.length > 0 ? (
                            linkSearchResults.map(result => (
                              <button
                                key={result.customerId}
                                type="button"
                                role="option"
                                className="add-store-live-results__item"
                                onMouseDown={event => {
                                  event.preventDefault();
                                  applyLinkCustomer(result);
                                }}
                              >
                                <span
                                  className="add-store-live-results__avatar"
                                  aria-hidden
                                >
                                  {(
                                    result.name ||
                                    result.customerId ||
                                    "?"
                                  )
                                    .split(/\s+/)
                                    .filter(Boolean)
                                    .slice(0, 2)
                                    .map(part => part[0]?.toUpperCase() || "")
                                    .join("") || "?"}
                                </span>
                                <span className="add-store-live-results__text">
                                  <span className="add-store-live-results__id">
                                    {result.customerId}
                                  </span>
                                  <span className="add-store-live-results__meta">
                                    {result.name || "Business customer"}
                                    {result.businessName
                                      ? ` · ${result.businessName}`
                                      : ""}
                                    {result.email ? ` · ${result.email}` : ""}
                                  </span>
                                </span>
                              </button>
                            ))
                          ) : linkLookingUp ? (
                            <div className="add-store-live-results__empty">
                              Searching…
                            </div>
                          ) : linkSearchEmpty ? (
                            <div className="add-store-live-results__empty">
                              No matching verified business customers.
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    {!linkCustomerPreview ? (
                      <span className="add-store-panel__hint">
                        Results update as you type. Only verified business
                        customers can be linked.
                      </span>
                    ) : null}

                    {linkCustomerPreview ? (
                      <>
                        <div className="add-store-customer">
                          <span
                            className="add-store-customer__avatar"
                            aria-hidden
                          >
                            {(
                              linkCustomerPreview.name ||
                              linkCustomerPreview.customerId ||
                              "?"
                            )
                              .split(/\s+/)
                              .filter(Boolean)
                              .slice(0, 2)
                              .map(part => part[0]?.toUpperCase() || "")
                              .join("") || "?"}
                          </span>
                          <div>
                            <div className="add-store-customer__name">
                              {linkCustomerPreview.name ||
                                linkCustomerPreview.customerId}
                            </div>
                            <div className="add-store-customer__meta">
                              {linkCustomerPreview.customerId}
                              {linkCustomerPreview.email
                                ? ` · ${linkCustomerPreview.email}`
                                : ""}
                              {linkCustomerPreview.businessName
                                ? ` · ${linkCustomerPreview.businessName}`
                                : ""}
                            </div>
                          </div>
                        </div>

                        <div className="add-store-section-title">
                          Store details
                        </div>

                        <FormGroup>
                          <Label
                            for="linkStoreName"
                            className="add-store-panel__label"
                          >
                            Store name
                          </Label>
                          <Input
                            id="linkStoreName"
                            type="text"
                            value={linkStoreName}
                            maxLength={120}
                            disabled={linkSubmitting}
                            onChange={event =>
                              setLinkStoreName(event.target.value)
                            }
                          />
                        </FormGroup>

                        <FormGroup className="mb-2">
                          <Label className="add-store-panel__label">
                            Default pickup location
                          </Label>
                          {environment.google_maps_key ? (
                            <CustomGooglePlacesAutocomplete
                              apiKey={environment.google_maps_key}
                              autocompletionRequest={addressSearchOptions}
                              addressType="pickup"
                              handleAddress={selectLinkPickupAddress}
                            />
                          ) : null}
                          <span className="add-store-panel__hint">
                            Search and select an address so drivers know where
                            to pick up.
                          </span>
                        </FormGroup>
                        <Row>
                          <Col md={8}>
                            <FormGroup>
                              <Label
                                for="linkPickupStreet"
                                className="add-store-panel__label"
                              >
                                Street
                              </Label>
                              <Input
                                id="linkPickupStreet"
                                type="text"
                                value={linkPickupAddress.street || ""}
                                disabled={linkSubmitting}
                                onChange={event =>
                                  setLinkPickupAddress(current => ({
                                    ...current,
                                    street: event.target.value,
                                  }))
                                }
                              />
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup>
                              <Label
                                for="linkPickupUnit"
                                className="add-store-panel__label"
                              >
                                Unit
                              </Label>
                              <Input
                                id="linkPickupUnit"
                                type="text"
                                value={linkPickupAddress.unit || ""}
                                disabled={linkSubmitting}
                                onChange={event =>
                                  setLinkPickupAddress(current => ({
                                    ...current,
                                    unit: event.target.value,
                                  }))
                                }
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={6}>
                            <FormGroup>
                              <Label
                                for="linkPickupCity"
                                className="add-store-panel__label"
                              >
                                City
                              </Label>
                              <Input
                                id="linkPickupCity"
                                type="text"
                                value={linkPickupAddress.city || ""}
                                disabled={linkSubmitting}
                                onChange={event =>
                                  setLinkPickupAddress(current => ({
                                    ...current,
                                    city: event.target.value,
                                  }))
                                }
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-0">
                              <Label
                                for="linkPickupPostal"
                                className="add-store-panel__label"
                              >
                                Postal code
                              </Label>
                              <Input
                                id="linkPickupPostal"
                                type="text"
                                value={linkPickupAddress.postalCode || ""}
                                disabled={linkSubmitting}
                                onChange={event =>
                                  setLinkPickupAddress(current => ({
                                    ...current,
                                    postalCode: event.target.value,
                                  }))
                                }
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </>
                    ) : null}
                  </div>
                )}
              </>
            ) : (
              <div className="add-store-panel">
                <div className="alert alert-success py-2 mb-3">
                  Link ready. Copy it now — it is shown only once.
                </div>
                <Label
                  for="createdInviteUrl"
                  className="add-store-panel__label"
                >
                  Onboarding link
                </Label>
                <Input
                  id="createdInviteUrl"
                  type="textarea"
                  rows={4}
                  value={createdInviteUrl}
                  readOnly
                  onFocus={event => event.currentTarget.select()}
                  style={{
                    fontFamily:
                      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                    fontSize: 13,
                    lineHeight: 1.45,
                    wordBreak: "break-all",
                    whiteSpace: "pre-wrap",
                    resize: "none",
                    background: "#fff",
                  }}
                />
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
                  <small className="text-muted mb-0">
                    {createdExpiresAt
                      ? `Expires ${new Date(createdExpiresAt).toLocaleString()}`
                      : "Single-use invite"}
                  </small>
                  <Button color="primary" size="sm" onClick={copyInviteLink}>
                    <i className={`bx ${copied ? "bx-check" : "bx-copy"} me-1`} />
                    {copied ? "Copied" : "Copy link"}
                  </Button>
                </div>
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              outline
              disabled={inviteModalBusy}
              onClick={() => closeInviteModal()}
            >
              {createdInviteUrl ? "Done" : "Cancel"}
            </Button>
            {!createdInviteUrl && addStoreMode === "invite" ? (
              <Button
                color="primary"
                disabled={inviteCreating}
                onClick={createInviteLink}
              >
                {inviteCreating ? (
                  <Puff
                    color="#FFD0B8"
                    height={18}
                    width={18}
                    wrapperClass="me-2"
                  />
                ) : null}
                Create link
              </Button>
            ) : null}
            {!createdInviteUrl &&
            addStoreMode === "existing" &&
            linkCustomerPreview ? (
              <Button
                color="primary"
                disabled={linkSubmitting || linkLookingUp}
                onClick={linkExistingCustomer}
              >
                {linkSubmitting ? (
                  <Puff
                    color="#FFD0B8"
                    height={18}
                    width={18}
                    wrapperClass="me-2"
                  />
                ) : null}
                Link store
              </Button>
            ) : null}
            {!createdInviteUrl &&
            addStoreMode === "existing" &&
            !linkCustomerPreview ? (
              <Button
                color="primary"
                disabled={
                  linkLookingUp ||
                  linkSubmitting ||
                  linkCustomerId.trim().length < 2
                }
                onClick={lookupExistingCustomer}
              >
                {linkLookingUp ? (
                  <Puff
                    color="#FFD0B8"
                    height={18}
                    width={18}
                    wrapperClass="me-2"
                  />
                ) : null}
                Use this ID
              </Button>
            ) : null}
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default Stores;
